import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import type { Service } from '@/lib/supabase/types';
import { localeField, localePath } from '@/lib/i18n/helpers';
import { getServiceIcon } from '@/lib/icons';
import { Reveal } from '@/components/site/reveal';

export function ServicesSection({
  locale,
  dict,
  services,
  compact = false,
}: {
  locale: Locale;
  dict: Dictionary;
  services: Service[];
  compact?: boolean;
}) {
  return (
    <section id="servicios" className="section-pad bg-background">
      <div className="container-tight">
        {!compact && (
          <div className="mb-10 flex items-start gap-3 sm:mb-12 sm:items-center sm:gap-4">
            <span className="shrink-0 font-serif text-3xl font-bold leading-none text-accent/20 sm:text-5xl">{dict.services.sectionTitle}</span>
            <div className="min-w-0 max-w-2xl">
              <span className="eyebrow">{dict.services.title}</span>
              <h2 className="mt-1 font-serif text-2xl font-bold text-primary sm:text-3xl md:text-4xl">
                {dict.services.title}
              </h2>
              <p className="mt-2 text-base text-muted-foreground sm:text-lg">{dict.services.subtitle}</p>
            </div>
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => {
            const Icon = getServiceIcon(s.icon);
            return (
              <Reveal key={s.id} delay={i * 90} as="article">
                <Link
                  to={localePath(`/areas/${s.slug}`, locale)}
                  className="card-surface group relative flex h-full flex-col overflow-hidden p-6"
                >
                  {/* accent shimmer that slides on hover */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  />
                  {/* radial accent glow on hover */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-accent/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
                  />

                  <div className="relative mb-5 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10 text-accent transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground group-hover:shadow-lg group-hover:shadow-accent/40">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-primary transition-colors group-hover:text-accent">
                    {localeField(s, 'title', locale)}
                  </h3>
                  <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-muted-foreground">
                    {localeField(s, 'summary', locale)}
                  </p>
                  <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-accent transition-all duration-300 group-hover:gap-2">
                    {dict.services.learnMore}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
